import { useId } from 'react';
import { Icon } from './Icon';
import styles from './PaginationFooter.module.css';

import { useT } from '@/i18n/languageStore';

export interface PaginationFooterProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Leaves out the "Show" page-size choice (a list with a fixed page size, e.g. Home). */
  hidePageSize?: boolean;
}

/**
 * The design's list footer (mobile/src/components/ui/PaginationFooter.tsx):
 * "Showing X–Y of Z", a page-size choice and previous / "Page N of M" / next.
 * The page size is the browser's own `<select>`, as in the design.
 */
export function PaginationFooter({
  page,
  pageSize,
  total,
  totalPages,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  hidePageSize = false,
}: PaginationFooterProps) {
  const t = useT();
  const sizeId = useId();
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  return (
    <nav className={styles.card} aria-label={t('Pagination')}>
      <div className={styles.top}>
        <p className={styles.text}>
          {t('Showing {rangeStart}–{rangeEnd} of {total}', { rangeStart, rangeEnd, total })}
        </p>
        {hidePageSize ? null : (
          <div className={styles.sizeRow}>
            <label className={styles.text} htmlFor={sizeId}>
              {t('Show')}
            </label>
            <select
              id={sizeId}
              className={styles.select}
              aria-label={t('Items per page')}
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className={styles.nav}>
        <button
          type="button"
          className={styles.pageButton}
          aria-label={t('Previous page')}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <Icon name="chevron-left" size={16} />
        </button>
        <p className={styles.pageLabel} aria-live="polite">
          {t('Page {page} of {totalPages}', { page, totalPages })}
        </p>
        <button
          type="button"
          className={styles.pageButton}
          aria-label={t('Next page')}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <Icon name="chevron-right" size={16} />
        </button>
      </div>
    </nav>
  );
}
